import { useCallback, useEffect, useState } from 'react';
import { toast } from 'material-react-toastify';
import {
  Box,
  Checkbox,
  Divider,
  FormControlLabel,
  IconButton,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { FilterKey } from '../../interfaces/Filter/FilterKey';
import { getFiltersByCategory } from '../../api/Filter';
import { useThemeColor } from '../../hooks/useThemeColor';
import { sharedColors } from '../../utils/Style';

type FilterAccordionProps = {
  filterKey: FilterKey;
  filterIDs: number[];
  onToggle: (filterID: number) => void;
};

const FilterAccordion = ({
  filterKey,
  filterIDs,
  onToggle,
}: FilterAccordionProps) => {
  const { primary } = useThemeColor();

  const [open, setOpen] = useState(false);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      <Box
        onClick={() => setOpen((x) => !x)}
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          cursor: 'pointer',
        }}
      >
        <Typography
          sx={{
            fontSize: '14px',
            fontWeight: 600,
            color: sharedColors.gray7,
            transition: 'color 0.5s ease',
            '&:hover': {
              color: primary,
            },
          }}
        >
          {filterKey.title}
        </Typography>

        <IconButton
          sx={{ transform: open ? 'rotate(180deg)' : undefined, p: 0 }}
        >
          <ExpandMoreIcon />
        </IconButton>
      </Box>

      {open && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            px: 1.5,
            gap: 1,
            mt: 0.5,
          }}
        >
          {filterKey.values.map((value) => (
            <FormControlLabel
              key={value.id}
              control={
                <Checkbox
                  size='small'
                  checked={filterIDs.includes(value.id)}
                  onChange={() => onToggle(value.id)}
                  sx={{ p: 0, mr: 0.5 }}
                />
              }
              label={
                <Typography sx={{ fontSize: '13px', fontWeight: 500 }}>
                  {value.title}
                </Typography>
              }
            />
          ))}
        </Box>
      )}
    </Box>
  );
};

type FilterSidebarProps = {
  categorySlug: string;
  filterIDs: number[];
  onChange: (newFilterIDs: number[]) => void;
};

export const FilterSidebar = ({
  categorySlug,
  filterIDs,
  onChange,
}: FilterSidebarProps) => {
  const [filters, setFilters] = useState<FilterKey[]>([]);

  useEffect(() => {
    getFiltersByCategory(categorySlug)
      .then(setFilters)
      .catch((err) => toast.error(err.message));
  }, []);

  const onToggle = useCallback(
    (filterID: number) => {
      onChange(
        filterIDs.includes(filterID)
          ? filterIDs.filter((x) => x !== filterID)
          : [...filterIDs, filterID],
      );
    },
    [filterIDs, onChange],
  );

  return filters.length ? (
    <Box
      sx={{
        width: 300,
        border: '1px solid #e1e1e1',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box sx={{ p: 2 }}>
        <Typography sx={{ fontSize: '15px', fontWeight: 600 }}>
          FİLTRE
        </Typography>
      </Box>

      <Divider />

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, p: 2 }}>
        {filters.map((filterKey) => (
          <FilterAccordion
            filterKey={filterKey}
            filterIDs={filterIDs}
            onToggle={onToggle}
          />
        ))}
      </Box>
    </Box>
  ) : null;
};
